// Vertical SLD layout constants and position calculation

export const LAYOUT = {
  canvasPadding: 40,

  // Total Load card
  loadCardW: 200,
  loadCardH: 80,

  // Bus Info bar
  busInfoW: 220,
  busInfoH: 50,
  busInfoGap: 16,

  // Vertical bus
  busWidth: 10,
  busTopGap: 20,

  // Meter indicators on bus
  meterRadius: 28,
  meterSpacing: 80,     // vertical space per meter on the bus
  meterBusGap: 20,      // gap between last meter and first device row

  // Device rows
  rowHeight: 140,

  // Cards
  cardW: 190,
  cardH: 100,
  cardRx: 10,

  // Branch lines
  branchGap: 70,
  dotRadius: 3.5,
  breakerRadius: 6,
} as const;

export interface MeterPosition {
  cx: number;    // center X (on the bus)
  cy: number;    // center Y
}

export interface LayoutPositions {
  canvasW: number;
  canvasH: number;
  centerX: number;
  loadCard: { x: number; y: number };
  busInfo: { x: number; y: number };
  busTop: number;
  busBottom: number;
  meters: MeterPosition[];
  rows: RowPosition[];
}

export interface RowPosition {
  y: number;           // center Y of the row
  genCardX: number;    // left edge of generator card
  bessCardX: number;   // left edge of BESS card
  genBranchStart: number;  // x where gen branch line starts (right edge of gen card)
  genBranchEnd: number;    // x where gen branch meets bus (left edge of bus)
  bessBranchStart: number; // x where bess branch starts (right edge of bus)
  bessBranchEnd: number;   // x where bess branch meets card (left edge of bess card)
}

export function calculateLayout(genCount: number, bessCount: number, meterCount: number): LayoutPositions {
  const L = LAYOUT;
  const rowCount = Math.max(genCount, bessCount, 1);

  const canvasW = L.canvasPadding * 2 + L.cardW + L.branchGap + L.busWidth + L.branchGap + L.cardW;
  const centerX = canvasW / 2;

  const loadCard = {
    x: centerX - L.loadCardW / 2,
    y: L.canvasPadding,
  };

  const busInfo = {
    x: centerX - L.busInfoW / 2,
    y: loadCard.y + L.loadCardH + L.busInfoGap,
  };

  const busTop = busInfo.y + L.busInfoH + L.busTopGap;

  // Meter zone: meters sit on the bus between bus info and device rows
  const meterZoneHeight = meterCount > 0
    ? meterCount * L.meterSpacing + L.meterBusGap
    : 0;

  const meters: MeterPosition[] = [];
  for (let i = 0; i < meterCount; i++) {
    meters.push({
      cx: centerX,
      cy: busTop + L.meterSpacing * 0.5 + i * L.meterSpacing,
    });
  }

  // Device rows start after meter zone
  const rowsTop = busTop + meterZoneHeight;
  const busBottom = rowsTop + rowCount * L.rowHeight + 20;

  const busLeftEdge = centerX - L.busWidth / 2;
  const busRightEdge = centerX + L.busWidth / 2;

  const genCardRight = busLeftEdge - L.branchGap;
  const genCardX = genCardRight - L.cardW;
  const bessCardX = busRightEdge + L.branchGap;

  const rows: RowPosition[] = [];
  for (let i = 0; i < rowCount; i++) {
    rows.push({
      y: rowsTop + i * L.rowHeight + L.rowHeight / 2,
      genCardX,
      bessCardX,
      genBranchStart: genCardRight,
      genBranchEnd: busLeftEdge,
      bessBranchStart: busRightEdge,
      bessBranchEnd: bessCardX,
    });
  }

  const canvasH = busBottom + L.canvasPadding;

  return { canvasW, canvasH, centerX, loadCard, busInfo, busTop, busBottom, meters, rows };
}
